/**
 * Copyright (c) 2020 Google Inc
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
 * FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
 * IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 * CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

const exposed = {};
if (location.search) {
  var a = document.createElement("a");
  a.href = location.href;
  a.search = "";
  history.replaceState(null, null, a.href);
}

function tweet_(url) {
  open(
    "https://twitter.com/intent/tweet?url=" + encodeURIComponent(url),
    "_blank"
  );
}
function tweet(anchor) {
  tweet_(anchor.getAttribute("href"));
}
expose("tweet", tweet);

function share(anchor) {
  var url = anchor.getAttribute("href");
  event.preventDefault();
  if (navigator.share) {
    navigator.share({
      url: url,
    });
  } else if (navigator.clipboard) {
    navigator.clipboard.writeText(url);
    message("Article URL copied to clipboard.");
  } else {
    tweet_(url);
  }
}
expose("share", share);

function toggleMenu(e) {
  document.body.classList.toggle("nav-open");
}
expose("toggleMenu", toggleMenu);

/**
 * Email obfuscation decoder
 * Decodes ROT13 encoded mailto links to prevent spam harvesting
 */
function rot13Decode(str) {
  return str.replace(/[a-zA-Z]/g, (c) =>
    String.fromCharCode((c <= "Z" ? 90 : 122) >= (c = c.charCodeAt(0) + 13) ? c : c - 26)
  );
}

function decodeEmails() {
  document.querySelectorAll("a[data-email]").forEach((link) => {
    const encoded = link.getAttribute("data-email");
    if (encoded) {
      link.href = rot13Decode(encoded);
      link.removeAttribute("data-email");
    }
  });
}

// Decode emails on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", decodeEmails);
} else {
  decodeEmails();
}

function message(msg) {
  var dialog = document.getElementById("message");
  dialog.textContent = msg;
  dialog.setAttribute("open", "");
  setTimeout(function () {
    dialog.removeAttribute("open");
  }, 3000);
}

function prefetch(e) {
  if (e.target.tagName != "A") {
    return;
  }
  if (e.target.origin != location.origin) {
    return;
  }
  /**
   * Return the given url with no fragment
   * @param {string} url potentially containing a fragment
   * @return {string} url without fragment
   */
  const removeUrlFragment = (url) => url.split("#")[0];
  if (
    removeUrlFragment(window.location.href) === removeUrlFragment(e.target.href)
  ) {
    return;
  }
  var l = document.createElement("link");
  l.rel = "prefetch";
  l.href = e.target.href;
  document.head.appendChild(l);
}
document.documentElement.addEventListener("mouseover", prefetch, {
  capture: true,
  passive: true,
});
document.documentElement.addEventListener("touchstart", prefetch, {
  capture: true,
  passive: true,
});

const GA_ID = document.documentElement.getAttribute("ga-id");
window.ga =
  window.ga ||
  function () {
    if (!GA_ID) {
      return;
    }
    (ga.q = ga.q || []).push(arguments);
  };
ga.l = +new Date();
ga("create", GA_ID, "auto");
ga("set", "transport", "beacon");
var timeout = setTimeout(
  (onload = function () {
    clearTimeout(timeout);
    ga("send", "pageview");
  }),
  1000
);

var ref = +new Date();
function ping(event) {
  var now = +new Date();
  if (now - ref < 1000) {
    return;
  }
  ga("send", {
    hitType: "event",
    eventCategory: "page",
    eventAction: event.type,
    eventLabel: Math.round((now - ref) / 1000),
  });
  ref = now;
}
addEventListener("pagehide", ping);
addEventListener("visibilitychange", ping);

/**
 * Injects a script into document.head
 * @param {string} src path of script to be injected in <head>
 * @return {Promise} Promise object that resolves on script load event
 */
const dynamicScriptInject = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.type = "text/javascript";
    document.head.appendChild(script);
    script.addEventListener("load", () => {
      resolve(script);
    });
  });
};

// Script web-vitals.js will be injected dynamically if user opts-in to sending CWV data.
const sendWebVitals = document.currentScript.getAttribute("data-cwv-src");

if (/web-vitals.js/.test(sendWebVitals)) {
  dynamicScriptInject(`${window.location.origin}/js/web-vitals.js`)
    .then(() => {
      webVitals.getCLS(sendToGoogleAnalytics);
      webVitals.getFID(sendToGoogleAnalytics);
      webVitals.getLCP(sendToGoogleAnalytics);
    })
    .catch((error) => {
      console.error(error);
    });
}

addEventListener(
  "click",
  function (e) {
    var button = e.target.closest("button");
    if (!button) {
      return;
    }
    ga("send", {
      hitType: "event",
      eventCategory: "button",
      eventAction: button.getAttribute("aria-label") || button.textContent,
    });
  },
  true
);
var selectionTimeout;
addEventListener(
  "selectionchange",
  function () {
    clearTimeout(selectionTimeout);
    var text = String(document.getSelection()).trim();
    if (text.split(/[\s\n\r]+/).length < 3) {
      return;
    }
    selectionTimeout = setTimeout(function () {
      ga("send", {
        hitType: "event",
        eventCategory: "selection",
        eventAction: text,
      });
    }, 2000);
  },
  true
);

// document.addEventListener("load", (e) => {
// const mainElement = document.querySelector("main");
// const progressBarTop = document.querySelector(".readingProgress");
// const percentageOfElement = Math.floor(
//   (mainElement.scrollHeight / document.body.scrollHeight) * 100
// );
// const options = {
//   threshold: 0,
// };
// const observer = new IntersectionObserver(function (entries, observer) {
//   entries.forEach((entry) => {
//     let heightOfvp = window.innerHeight;
//     let { height: heightOfelement } = entry.target.getBoundingClientRect();
//     let progressPercentage = 0;
//     if (entry.isIntersecting) {
//       progressPercentage = Math.round(
//         (window.scrollY / (heightOfelement - heightOfvp)) * percentageOfElement
//       );
//       // progressBarTop.style.width = progressPercentage + "%";
//       progressBarTop.style.transform = `translate(-${progressPercentage}vw, 0)`;
//     }
//   });
// }, options);
// observer.observe(mainElement);
// });

const progress = document.getElementById("readingProgress");
if (window.ResizeObserver && progress) {
  let timeOfLastScroll = 0;
  let requestedAniFrame = false;
  function scroll() {
    if (!requestedAniFrame) {
      requestAnimationFrame(updateProgress);
      requestedAniFrame = true;
    }
    timeOfLastScroll = Date.now();
  }
  addEventListener("scroll", scroll);

  let winHeight = 1000;
  let bottom = 10000;
  function updateProgress() {
    requestedAniFrame = false;
    let percent = Math.min(
      (document.scrollingElement.scrollTop / (bottom - winHeight)) * 100,
      100
    );
    progress.style.transform = `translate(-${100 - percent}vw, 0)`;
    if (Date.now() - timeOfLastScroll < 3000) {
      requestAnimationFrame(updateProgress);
      requestedAniFrame = true;
    }
  }

  new ResizeObserver(() => {
    bottom =
      document.scrollingElement.scrollTop +
      document.querySelector("#comments,footer").getBoundingClientRect().top;
    winHeight = window.innerHeight;
    scroll();
  }).observe(document.body);
}

function expose(name, fn) {
  exposed[name] = fn;
}

addEventListener("click", (e) => {
  const handler = e.target.closest("[data-on-click]");
  if (!handler) {
    return;
  }
  e.preventDefault();
  const name = handler.getAttribute("data-on-click");
  const fn = exposed[name];
  if (!fn) {
    throw new Error("Unknown handler" + name);
  }
  fn(handler);
});

// There is a race condition here if an image loads faster than this JS file. But
// - that is unlikely
// - it only means potentially more costly layouts for that image.
// - And so it isn't worth the querySelectorAll it would cost to synchronously check
//   load state.
function attachImageLoadFix() {
  document.body.addEventListener(
    "load",
    (e) => {
      if (e.target.tagName != "IMG") {
        return;
      }
      // Ensure the browser doesn't try to draw the placeholder when the real image is present.
      e.target.style.backgroundImage = "none";
    },
    /* capture */ "true"
  );
}
// This script has the `async` attribute, so it can execute before the parser
// reaches <body> — document.body would then be null.
if (document.body) {
  attachImageLoadFix();
} else {
  document.addEventListener("DOMContentLoaded", attachImageLoadFix);
}

// import './plugins/youtube-embed.js';
/**
 * YOUTUBE
 *
 * HTML ALTERNATE
 * https://css-tricks.com/lazy-load-embedded-youtube-videos/
 */
window.addEventListener("load", (e) => {
  let YouTubeContainers = document.querySelectorAll(".embed-youtube");

  // Iterate over every YouTube container you may have
  for (let i = 0; i < YouTubeContainers.length; i++) {
    let container = YouTubeContainers[i];
    let imageSource =
      "https://img.youtube.com/vi/" +
      container.dataset.videoId +
      "/sddefault.jpg";

    // Load the Thumbnail Image asynchronously
    let image = new Image();
    image.src = imageSource;
    image.addEventListener("load", function () {
      container.appendChild(image);
    });

    // When the user clicks on the container, load the embedded YouTube video
    container.addEventListener("click", function () {
      let iframe = document.createElement("iframe");

      iframe.setAttribute("frameborder", "0");
      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute(
        "allow",
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      );
      // Important: add the autoplay GET parameter, otherwise the user would need to click over the YouTube video again to play it
      iframe.setAttribute(
        "src",
        "https://www.youtube.com/embed/" +
          this.dataset.videoId +
          "?rel=0&showinfo=0&autoplay=1"
      );

      // Clear Thumbnail and load the YouTube iframe
      this.innerHTML = "";
      this.appendChild(iframe);
    });
  }
});

/**
 * GLightbox Integration
 * Conditional loading for image/video lightbox functionality
 */

// Check if page has elements that need lightbox functionality
function needsLightbox() {
  // Check for images with lightbox attributes (both data-lightbox and data-glightbox)
  const lightboxImages = document.querySelectorAll(
    "img[data-lightbox], a[data-lightbox], img[data-glightbox], a[data-glightbox]"
  );
  // Check for gallery elements
  const galleries = document.querySelectorAll("[data-gallery]");
  // Check for images in kanun containers (legal documents)
  const kanunImages = document.querySelectorAll(
    ".kanun-content img, details.kanun img"
  );
  // Check for images in post content (blog posts with actual content images)
  // Excludes: card images, header images, decorative images
  const postContentImages = document.querySelectorAll(
    ".post-content img, .page-content img"
  );

  return (
    lightboxImages.length > 0 ||
    galleries.length > 0 ||
    kanunImages.length > 0 ||
    postContentImages.length > 0
  );
}

// Load GLightbox CSS and JS dynamically
function loadGLightbox() {
  return new Promise((resolve, reject) => {
    // Load CSS first
    const cssLink = document.createElement("link");
    cssLink.rel = "stylesheet";
    cssLink.href = `${window.location.origin}/css/glightbox.min.css`;
    document.head.appendChild(cssLink);

    // Load custom CSS
    const customCssLink = document.createElement("link");
    customCssLink.rel = "stylesheet";
    customCssLink.href = `${window.location.origin}/css/glightbox-custom.css`;
    document.head.appendChild(customCssLink);

    // Then load JS using existing dynamicScriptInject function
    dynamicScriptInject(`${window.location.origin}/js/glightbox.min.js`)
      .then(resolve)
      .catch(reject);
  });
}

// Add lightbox attributes to eligible images
function prepareImagesForLightbox() {
  // Add lightbox to kanun container images
  const kanunImages = document.querySelectorAll(
    ".kanun-content img, details.kanun img"
  );
  kanunImages.forEach((img, index) => {
    if (!img.hasAttribute("data-glightbox")) {
      const alt =
        img.getAttribute("alt") || `Legal Document Image ${index + 1}`;
      img.setAttribute("data-gallery", "legal-docs");
      img.setAttribute("data-glightbox", `title: ${alt}`);
      img.style.cursor = "pointer";
    }
  });

  // Add lightbox to post/page content images that are large enough
  // Uses specific content classes to avoid decorative/card images
  const contentImages = document.querySelectorAll(
    ".post-content img, .page-content img"
  );
  contentImages.forEach((img, index) => {
    if (!img.hasAttribute("data-glightbox") && img.naturalWidth > 400) {
      const alt = img.getAttribute("alt") || `Content Image ${index + 1}`;
      img.setAttribute("data-gallery", "content");
      img.setAttribute("data-glightbox", `title: ${alt}`);
      img.style.cursor = "pointer";
    }
  });
}

// Initialize GLightbox with custom configuration
function initializeGLightbox() {
  if (typeof GLightbox === "undefined") {
    console.error("GLightbox library not loaded");
    return;
  }

  const lightbox = GLightbox({
    touchNavigation: true,
    loop: false,
    autoplayVideos: false,
    autofocusVideos: true,
    closeButton: true,
    touchFollowAxis: true,
    keyboardNavigation: true,
    closeOnOutsideClick: true,
    startAt: 0,
    videosWidth: "960px",
    beforeSlideChange: function (prev, current) {
      // Add any custom logic before slide changes
      return true;
    },
    afterSlideChange: function (prev, current) {
      // Add any custom logic after slide changes
    },
  });

  return lightbox;
}

// Main function to setup GLightbox if needed
function setupLightbox() {
  if (!needsLightbox()) {
    return;
  }

  loadGLightbox()
    .then(() => {
      // Prepare images for lightbox
      prepareImagesForLightbox();

      // Initialize GLightbox
      const lightbox = initializeGLightbox();

      // Store reference globally for debugging/external access
      window.lawFirmLightbox = lightbox;

      console.log("GLightbox initialized for Akkuş Avukatlık");
    })
    .catch((error) => {
      console.error("Failed to load GLightbox:", error);
    });
}

// Initialize lightbox after DOM is fully loaded
window.addEventListener("load", setupLightbox);

/**
 * Cloudflare Analytics - Privacy-friendly event tracking
 * Tracks: phone, whatsapp, email, buttons, forms, outbound links
 * Features: debounce, rate limiting, sanitization, page context
 */
(function initAnalytics() {
  const root = document.documentElement;
  const DOMAIN = root.getAttribute("data-domain");
  const CF_TOKEN = root.getAttribute("data-cf-token");
  const IS_PROD = location.hostname === DOMAIN;

  if (!IS_PROD) return;

  // Load Cloudflare beacon
  if (CF_TOKEN) {
    const s = document.createElement("script");
    s.defer = true;
    s.src = "https://static.cloudflareinsights.com/beacon.min.js";
    s.setAttribute("data-cf-beacon", JSON.stringify({ token: CF_TOKEN }));
    document.head.appendChild(s);
  }

  // Event tracker with debounce & rate limiting
  const tracker = (function () {
    const MAX_PER_MIN = 20;
    const DEBOUNCE = 300;
    let count = 0;
    let lastTime = 0;
    let resetTimer = null;

    const sanitize = (str) =>
      str.toLowerCase().replace(/[^a-z0-9\s-]/gi, "").replace(/\s+/g, "-").slice(0, 50);

    const getPage = () => encodeURIComponent(location.pathname);

    const track = (category, action) => {
      const now = Date.now();
      if (now - lastTime < DEBOUNCE || count >= MAX_PER_MIN) return;

      lastTime = now;
      count++;
      if (!resetTimer) {
        resetTimer = setTimeout(() => { count = 0; resetTimer = null; }, 60000);
      }

      const url = `/events/${category}/${sanitize(action)}?p=${getPage()}`;
      navigator.sendBeacon ? navigator.sendBeacon(url) : (new Image().src = url);
    };

    return { track };
  })();

  // Helper: get location from data-track or data-section
  const getLocation = (el) => {
    if (el.getAttribute("data-track")) return el.getAttribute("data-track");
    const section = el.closest("[data-section]");
    return section ? section.getAttribute("data-section") : "genel";
  };

  // Click handler
  document.addEventListener("click", function (e) {
    const t = e.target;

    // Phone calls (highest priority conversion)
    const phone = t.closest('a[href^="tel:"]');
    if (phone) {
      const num = phone.getAttribute("href").replace(/\D/g, "").slice(-4);
      return tracker.track("telefon", `${getLocation(phone)}-${num}`);
    }

    // WhatsApp
    const wa = t.closest('a[href^="https://wa.me"]');
    if (wa) return tracker.track("whatsapp", getLocation(wa));

    // Email
    const mail = t.closest('a[href^="mailto:"]');
    if (mail) return tracker.track("email", getLocation(mail));

    // Tracked links
    const tracked = t.closest("a[data-track]");
    if (tracked) return tracker.track("link", tracked.getAttribute("data-track"));

    // Buttons
    const btn = t.closest("button");
    if (btn) {
      const label = btn.getAttribute("data-track") ||
        btn.getAttribute("aria-label") ||
        btn.textContent.trim().slice(0, 30);
      return tracker.track("button", label);
    }

    // Outbound links
    const link = t.closest("a[href]");
    if (link) {
      const href = link.getAttribute("href");
      if (href && href.startsWith("http") && !href.includes(DOMAIN)) {
        try {
          tracker.track("outbound", new URL(href).hostname.replace("www.", ""));
        } catch (_) {}
      }
    }
  }, true);

  // Form submissions
  document.addEventListener("submit", function (e) {
    const form = e.target.closest("form");
    if (form) {
      const name = form.getAttribute("data-track") ||
        form.getAttribute("name") ||
        form.getAttribute("id") ||
        "form";
      tracker.track("form", name);
    }
  }, true);
})();
